import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, FlatList, TouchableOpacity,
  ActivityIndicator, Alert, TextInput, Modal, Switch
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import CONFIG from '../../config/Config';
import CustomAlert from '../../components/CustomAlert';

export default function OnlineMagazaScreen() {
  const navigation = useNavigation();
  const [urunler, setUrunler] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [searchText, setSearchText] = useState('');

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedUrun, setSelectedUrun] = useState(null);
  const [formMinGram, setFormMinGram] = useState('');
  const [formPopuler, setFormPopuler] = useState(false);
  const [formAvgGram, setFormAvgGram] = useState('');
  const [formUrunTuru, setFormUrunTuru] = useState('');
  const [formOnline, setFormOnline] = useState(false);
  const [formAciklama, setFormAciklama] = useState('');

  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const isBirimSabit = selectedUrun?.Birim?.toUpperCase() === 'PAKET' || selectedUrun?.Birim?.toUpperCase() === 'ADET';

  useEffect(() => {
    navigation.setOptions({ title: 'Online Mağaza' });
  }, [navigation]);

  const fetchUrunler = async () => {
    setIsLoading(true);
    setShowError(false);

    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/urunler`, { headers: { 'X-App-Env': CONFIG.ENV } });
      const result = await response.json();
      if (result.success) {
        setUrunler(result.data || []);
      } else {
        setErrorMsg(result.message || 'Ürünler yüklenemedi.');
        setShowError(true);
      }
    } catch (error) {
      setErrorMsg('Sunucuya bağlanılamadı.');
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useFocusEffect(
    useCallback(() => {
      fetchUrunler();
    }, [])
  );

  const turkceNormalize = (metin) => {
    if (!metin) return '';
    return metin
      .replace(/ç/g, 'c').replace(/Ç/g, 'c')
      .replace(/ğ/g, 'g').replace(/Ğ/g, 'g')
      .replace(/ı/g, 'i').replace(/İ/g, 'i')
      .replace(/ö/g, 'o').replace(/Ö/g, 'o')
      .replace(/ş/g, 's').replace(/Ş/g, 's')
      .replace(/ü/g, 'u').replace(/Ü/g, 'u')
      .toLowerCase()
      .trim();
  };



  const openEditModal = (urun) => {
    setSelectedUrun(urun);
    setFormMinGram(urun.MinGram?.toString() ?? '');
    setFormPopuler(Boolean(urun.Populer));
    setFormAvgGram(urun.AvgGram?.toString() ?? '');
    setFormAciklama(urun.Aciklama || '');
    const urunTuru = urun.UrunTuru?.trim();

    let varsayilanTur = 'weight';
    if (urun.Birim) {
      const birimDegeri = urun.Birim.toUpperCase();
      if (birimDegeri === 'PAKET' || birimDegeri === 'ADET') {
        varsayilanTur = birimDegeri.toLowerCase(); // 'paket' veya 'adet' olur
      }
    }


    setFormUrunTuru(urunTuru && urunTuru.length > 0 ? urunTuru : varsayilanTur);
    setFormOnline(urun.Online === 1 || urun.Online === '1');
    setEditModalVisible(true);
  };

  const saveEditUrun = async () => {
    if (!selectedUrun) return;

    const birimDegeri = selectedUrun?.Birim?.toUpperCase();
    const isBirimAdetPaket = birimDegeri === 'PAKET' || birimDegeri === 'ADET';

    // Gönderilecek ürün türünü burada kesinleştiriyoruz:
    // Ürün paket/adet ise birimin küçük harfli halini al ('paket' veya 'adet')
    // Değilse formdaki değeri al, o da yoksa mecburen 'weight' yap.
    const urunTuru = isBirimAdetPaket ? birimDegeri.toLowerCase() : (formUrunTuru || 'weight');

    // Ürün ADET veya PAKET DEĞİLSE gramaj zorunluluklarını kontrol et
    if (!isBirimAdetPaket) {
      if (urunTuru === 'weight' && !formMinGram?.trim()) {
        setErrorMsg('KG için minimum gram girilmelidir.');
        setShowError(true);
        return;
      }

      if (urunTuru !== 'weight' && !formAvgGram?.trim()) {
        setErrorMsg('Dilim/Kangal için ortalama gramaj girilmelidir.');
        setShowError(true);
        return;
      }
    }

    setIsUpdating(true);
    try {
      const formData = new FormData();
      formData.append('id', selectedUrun.id);
      formData.append('online', formOnline ? '1' : '0');
      formData.append('populer', formPopuler ? '1' : '0');
      formData.append('aciklama', formAciklama);

      // Doğru türü API'ye gönderiyoruz (paket, adet, weight, dilim veya kangal)
      formData.append('urunturu', urunTuru);

      // Gramaj verilerini duruma göre ekliyoruz
      if (!isBirimAdetPaket) {
        if (urunTuru === 'weight') {
          formData.append('mingram', formMinGram);
        }
        formData.append('avggram', formAvgGram);
      } else {
        // Ürün ADET/PAKET ise API'nin gramaj eksikliğinden hata vermemesi için
        // varsa mevcut değerleri, yoksa '0' gönderiyoruz.
        formData.append('mingram', selectedUrun.MinGram ? selectedUrun.MinGram.toString() : '0');
        formData.append('avggram', selectedUrun.AvgGram ? selectedUrun.AvgGram.toString() : '0');
      }

      const response = await fetch(`${CONFIG.API_BASE_URL}/urun_duzenle`, {
        method: 'POST',
        headers: { 'X-App-Env': CONFIG.ENV },
        body: formData,
      });
      const result = await response.json();

      if (result.success) {
        // Ekrandaki listeyi güncelle
        setUrunler((prev) => prev.map((u) => {
          if (u.id !== selectedUrun.id) return u;
          return {
            ...u,
            Online: formOnline ? 1 : null,
            Populer: formPopuler ? 1 : 0,
            MinGram: !isBirimAdetPaket && urunTuru === 'weight' ? (formMinGram ? parseInt(formMinGram, 10) : null) : u.MinGram,
            AvgGram: !isBirimAdetPaket && formAvgGram ? parseInt(formAvgGram, 10) : u.AvgGram,
            UrunTuru: urunTuru,
            Aciklama: formAciklama,
          };
        }));
        setEditModalVisible(false);
      } else {
        setErrorMsg(result.message || 'Ürün düzenlenemedi.');
        setShowError(true);
      }
    } catch (error) {
      setErrorMsg('Sunucuya bağlanılamadı.');
      setShowError(true);
    } finally {
      setIsUpdating(false);
    }
  };

const renderItem = ({ item }) => {
    const isOnline = item.Online === 1 || item.Online === '1';
    
    // Tür metnini belirliyoruz
    const getUrunTuru = () => {
      if (item.UrunTuru === 'weight') return 'KG';
      if (item.UrunTuru === 'dilim') return 'Dilim';
      if (item.UrunTuru === 'kangal') return 'Kangal';
      if (item.UrunTuru === 'paket') return 'Paket';
      if (item.UrunTuru === 'adet') return 'Adet';
      return null;
    };

    const urunTuru = getUrunTuru();

    return (
      <TouchableOpacity
        style={[styles.urunItem, { borderLeftColor: isOnline ? '#4CAF50' : '#90A4AE' }]}
        onPress={() => openEditModal(item)}
        disabled={isUpdating}
      >
        {/* Sol Kısım: Ürün Adı ve Detay Etiketleri */}
        <View style={styles.urunContent}>
          <Text style={styles.urunAdi}>{item.UrunAdi || 'Adı olmayan ürün'}</Text>
          
          <View style={styles.urunEtiketler}>
            {/* Tür geçerliyse göster */}
            {urunTuru && (
              <View style={styles.etiket}>
                <Text style={styles.etiketMetin}>Tür: {urunTuru}</Text>
              </View>
            )}

            {/* MinGram 0'dan büyükse göster */}
            {item.MinGram > 0 && (
              <View style={styles.etiket}>
                <Text style={styles.etiketMetin}>Min: {item.MinGram}g</Text>
              </View>
            )}

            {/* AvgGram 0'dan büyükse göster */}
            {item.AvgGram > 0 && (
              <View style={styles.etiket}>
                <Text style={styles.etiketMetin}>Ort: {item.AvgGram}g</Text>
              </View>
            )}
          </View>
        </View>

        {/* Sağ Kısım: Popüler ve Online Durumu (Alt Alta Hizalı) */}
        <View style={{ alignItems: 'flex-end', justifyContent: 'center' }}>
          
          {/* Popüler Rozeti */}
          {!!item.Populer && (
            <View style={styles.statusRow}>
              <Ionicons name="star" size={18} color="#FFC107" style={{ marginRight: 4 }} />
              <Text style={[styles.statusText, { color: '#FFC107' }]}>Popüler</Text>
            </View>
          )}

          {/* Online Durumu */}
          <View style={styles.statusRow}>
            <Ionicons 
              name={isOnline ? 'cloud-done' : 'cloud-offline'} 
              size={18} 
              color={isOnline ? '#4CAF50' : '#90A4AE'} 
              style={{ marginRight: 13 }} // İkonun sağına 6 birim boşluk ekledik
            />
            {isOnline && <Text style={[styles.statusText, { color: '#4CAF50' }]}>Online</Text>}
          </View>
          
        </View>
      </TouchableOpacity>
    );
  };

  const filtrelenmisUrunler = urunler
    .filter((item) => turkceNormalize(item.UrunAdi).includes(turkceNormalize(searchText)))
    .sort((a, b) => {
      const aOnline = a.Online === 1 || a.Online === '1';
      const bOnline = b.Online === 1 || b.Online === '1';
      if (aOnline && !bOnline) return -1;
      if (!aOnline && bOnline) return 1;
      return 0;
    });

  return (
    <SafeAreaView style={styles.safeArea} edges={['left', 'right', 'bottom']}>
      <View style={styles.headerContainer}>
        <Text style={styles.headerText}>Online mağaza ürün listesi</Text>
        <Text style={styles.helperText}>Ürüne dokunarak düzenleme yapın.</Text>
      </View>

      <View style={styles.searchContainer}>
        <Ionicons name="search-outline" size={18} color="#7f8c8d" />
        <TextInput
          style={styles.searchInput}
          placeholder="Ürün adı ile filtrele..."
          placeholderTextColor="#b0bcc7"
          value={searchText}
          onChangeText={setSearchText}
          autoCapitalize="none"
          autoCorrect={false}
        />
        {searchText.length > 0 && (
          <TouchableOpacity onPress={() => setSearchText('')}>
            <Ionicons name="close-circle" size={18} color="#7f8c8d" />
          </TouchableOpacity>
        )}
      </View>

      {isLoading ? (
        <View style={styles.loadingBox}>
          <ActivityIndicator size="large" color="#FF9800" />
          <Text style={styles.loadingText}>Ürünler yükleniyor...</Text>
        </View>
      ) : (
        <FlatList
          data={filtrelenmisUrunler}
          keyExtractor={(item) => item.id?.toString() ?? Math.random().toString()}
          renderItem={renderItem}
          contentContainerStyle={{ padding: 12 }}
          ItemSeparatorComponent={() => <View style={{ height: 8 }} />}
          ListEmptyComponent={<Text style={styles.emptyText}>Ürün bulunamadı.</Text>}
        />
      )}

      <Modal
        visible={editModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setEditModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>Ürün Düzenleme {selectedUrun?.Birim ? `(${selectedUrun.Birim})` : ''}</Text>
            
            <View style={styles.switchRow}>
              <Text style={styles.modalLabel}>Online</Text>
              <Switch value={formOnline} onValueChange={setFormOnline} />
            </View>

            {/* Popüler seçeneği de her zaman görünür olacak */}
            <View style={styles.switchRow}>
              <Text style={styles.modalLabel}>Popüler</Text>
              <Switch value={formPopuler} onValueChange={setFormPopuler} />
            </View>

            {/* Sadece ADET veya PAKET DEĞİLSE Gramaj ve Ürün Türü alanlarını göster */}
            {!(selectedUrun?.Birim?.toUpperCase() === 'PAKET' || selectedUrun?.Birim?.toUpperCase() === 'ADET') && (
              <>
                <Text style={styles.modalLabel}>Min Gram</Text>
                <TextInput
                  style={[styles.modalInput, formUrunTuru !== 'weight' && styles.modalInputDisabled]}
                  keyboardType="numeric"
                  value={formMinGram}
                  onChangeText={setFormMinGram}
                  editable={formUrunTuru === 'weight'}
                />

                <Text style={styles.modalLabel}>Ortalama Gramaj</Text>
                <TextInput
                  style={[styles.modalInput, formUrunTuru === 'weight' && styles.modalInputDisabled]}
                  keyboardType="numeric"
                  value={formAvgGram}
                  onChangeText={setFormAvgGram}
                  editable={formUrunTuru !== 'weight'}
                />

                <Text style={styles.modalLabel}>Ürün Türü</Text>
                <View style={styles.urunTuruContainer}>
                  {[
                    { label: 'KG', value: 'weight' },
                    { label: 'Dilim', value: 'dilim' },
                    { label: 'Kangal', value: 'kangal' },
                  ].map((opt) => (
                    <TouchableOpacity
                      key={opt.value}
                      style={[
                        styles.urunTuruOption,
                        formUrunTuru === opt.value && styles.urunTuruOptionActive,
                      ]}
                      onPress={() => setFormUrunTuru(opt.value)}
                    >
                      <Text
                        style={[
                          styles.urunTuruOptionText,
                          formUrunTuru === opt.value && styles.urunTuruOptionTextActive,
                        ]}
                      >
                        {opt.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </>
            )}

            <Text style={styles.modalLabel}>Açıklama</Text>
            <TextInput
              style={[styles.modalInput, { height: 80, textAlignVertical: 'top' }]} // Çok satırlı görünüm için
              value={formAciklama}
              onChangeText={setFormAciklama}
              placeholder="Örn: Taze Çekim..."
              multiline={true}
              numberOfLines={3}
            />

            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.modalBtn, { backgroundColor: '#E53935' }]}
                onPress={() => setEditModalVisible(false)}
                disabled={isUpdating}
              >
                <Text style={styles.modalBtnText}>İptal</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalBtn, { backgroundColor: '#4CAF50' }]}
                onPress={saveEditUrun}
                disabled={isUpdating}
              >
                <Text style={styles.modalBtnText}>{isUpdating ? 'Kaydediliyor...' : 'Kaydet'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {showError && (
        <CustomAlert
          visible={showError}
          title="Hata"
          message={errorMsg}
          type="error"
          confirmText="Tamam"
          onConfirm={() => setShowError(false)}
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#F5F7FA' },
  headerContainer: { padding: 14, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#ecf0f1' },
  headerText: { fontSize: 16, fontWeight: '700', color: '#2c3e50' },
  helperText: { fontSize: 12, color: '#7f8c8d', marginTop: 4 },
  loadingBox: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 10, color: '#7f8c8d' },
  urunItem: { flexDirection: 'row', alignItems: 'center', borderLeftWidth: 5, backgroundColor: '#fff', borderRadius: 12, padding: 12, elevation: 1 },
  urunContent: { flex: 1 },
  urunAdi: { fontSize: 15, color: '#2c3e50', fontWeight: '700' },
  urunAltText: { fontSize: 12, color: '#7f8c8d', marginTop: 4 },
  urunEtiketler: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 6 },
  etiket: { backgroundColor: '#f1f5f9', borderRadius: 10, paddingHorizontal: 8, paddingVertical: 4, marginBottom: 4 },
  etiketMetin: { fontSize: 11, color: '#475569', fontWeight: '600' },
  etiketOnline: { backgroundColor: '#e6f4ea' },
  etiketOffline: { backgroundColor: '#f5f5f5' },
  statusRow: { flexDirection: 'row', alignItems: 'center' },
  statusText: { fontSize: 12, fontWeight: '600' },

  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderColor: '#e9ecef',
    borderWidth: 1,
    borderRadius: 12,
    margin: 12,
    paddingHorizontal: 10,
    height: 42,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#2c3e50',
    paddingVertical: 8,
  },

  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    width: '90%',
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 18,
    elevation: 6,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 12,
    color: '#2c3e50',
  },
  modalLabel: {
    fontSize: 12,
    color: '#7f8c8d',
    marginTop: 10,
    marginBottom: 4,
  },
  modalInput: {
    borderWidth: 1,
    borderColor: '#e9ecef',
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 14,
    color: '#2c3e50',
    backgroundColor: '#fff',
  },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 10,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  urunTuruContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
    marginTop: 8,
  },
  urunTuruOption: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#e9ecef',
    borderRadius: 8,
    paddingVertical: 8,
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  urunTuruOptionActive: {
    borderColor: '#4CAF50',
    backgroundColor: '#E8F5E9',
  },
  urunTuruOptionText: {
    color: '#2c3e50',
    fontWeight: '600',
  },
  urunTuruOptionTextActive: {
    color: '#388E3C',
  },
  modalBtn: {
    flex: 1,
    marginHorizontal: 4,
    paddingVertical: 10,
    borderRadius: 10,
    alignItems: 'center',
  },
  modalBtnText: {
    color: '#fff',
    fontWeight: '700',
  },

  modalInputDisabled: {
    backgroundColor: '#f2f2f2',
    color: '#99a5b0',
  },
  emptyText: { textAlign: 'center', color: '#95a5a6', marginTop: 40, fontSize: 14 },
});
